/* eslint-disable no-nested-ternary */
import { Button, Card, CardContent, CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { uploadFile } from '../../../../service/upload.file.service';
import TestInfoCard from '../../../../components/TestInfoCard';
import PencilIcon from '../../../../svg/PencilIcon';
import palette from '../../../../theme/palette';
import GeneralInstruction from '../../../../components/GeneralInstruction';
import NameAndChangeToScreenCard from '../../../../components/NameAndChangeToScreenCard';
import EyeIcon from '../../../../svg/EyeIcon';
import FileUploadIcon from '../../../../svg/FileUploadIcon';
import DeleteIcon from '../../../../svg/DeleteIcon';
import '../../../../css/upcomingTest.css';
import Header from '../../../../components/Header';
import PreviewAsgn from './Teacher.PreviewAsgn';
import UploadAsgn from './Teacher.UploadAsgn';
import {
  deleteAssignment,
  getAssignmentById,
  updateAssignment
} from '../../../../service/assignment.service';
import { ConvertToBase64, GetDurationBetweenTwoDate } from '../../../../helper/commonHelper';
import AddOrUpdateAssignment from '../../../../components/AddOrUpdateAssignment';

export default function UpcomingAsgnDetails({ asgnId, onBack }) {
  const [asgnDetail, setAsgnDetail] = useState({});
  const classData = useSelector((state) => state.classes.selectedClass);
  const currentUser = useSelector((state) => state.auth.currentUser);
  const [selectedScreen, setSelScreen] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [btnDisable, setBtnDisable] = useState(false);
  const [openEditDialog, setEditDialog] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const fetchAsgnData = async () => {
    const res = await getAssignmentById(asgnId);
    if (res) {
      setAsgnDetail(res);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAsgnData();
  }, []);

  const onDeleteClick = async () => {
    setBtnDisable(true);
    const res = await deleteAssignment(asgnId);
    if (res) {
      onBack();
    }
    setBtnDisable(false);
  };

  const updateAsgnWithSolution = async (file) => {
    const key = enqueueSnackbar('File Uploading', {
      variant: 'loading',
      persist: true,
      className: 'loading-variant'
    });
    const base64 = await ConvertToBase64(file);
    const fileObj = {
      base64,
      fileType: file.type,
      fileName: file.name,
      uploadFor: 'assignment',
      userId: currentUser.id,
      orgId: classData.orgID
    };
    const fileRes = await uploadFile(classData.id, fileObj);
    if (fileRes) {
      const assignmentObj = {
        isSolutionUploaded: true,
        solutionURL: fileRes.fileURL
      };
      const asgnRes = await updateAssignment(asgnId, assignmentObj);
      if (asgnRes) {
        closeSnackbar(key);
        enqueueSnackbar('Solution Uploaded', {
          variant: 'success'
        });
      } else {
        closeSnackbar(key);
        enqueueSnackbar('Please try again later', {
          variant: 'error'
        });
      }
    } else {
      closeSnackbar(key);
      enqueueSnackbar('Please try again later', {
        variant: 'error'
      });
    }
  };

  return (
    <div style={{ marginTop: 20 }}>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '20% 0' }}>
          <CircularProgress />
        </div>
      ) : selectedScreen === null ? (
        <>
          <Header title={asgnDetail.name} onBack={onBack} />
          <div className="nameAndActionContainer">
            <div
              style={{
                borderRadius: 50,
                backgroundColor: palette.secondary.main,
                color: 'white',
                padding: 10,
                fontSize: 12
              }}
            >
              {`Starts in ${GetDurationBetweenTwoDate(new Date(), asgnDetail.startDateTime)}`}
            </div>
            <div className="testActionButtons">
              <Button disabled={btnDisable} onClick={() => setEditDialog(true)}>
                <PencilIcon color={palette.primary.main} style={{ marginRight: 10 }} /> Edit
                Assignment
              </Button>
              <Button
                style={{ color: 'red', marginLeft: 10 }}
                disabled={btnDisable}
                onClick={() => onDeleteClick()}
              >
                <DeleteIcon color="red" style={{ marginRight: 10 }} /> Delete Assignment
              </Button>
            </div>
          </div>
          <TestInfoCard
            createdOn={moment(asgnDetail.createdAt).format('DD MMM, LT')}
            startTime={moment(asgnDetail.startDateTime).format('DD MMM, LT')}
            submissionDate={moment(asgnDetail.endDateTime).format('DD MMM, LT')}
          />
          {asgnDetail?.description && (
            <GeneralInstruction instructionList={asgnDetail?.description.split(',')} />
          )}
          <Card style={{ marginTop: 25 }}>
            <CardContent>
              <NameAndChangeToScreenCard
                title="Preview Assignment"
                icon={<EyeIcon color={palette.secondary.main} />}
                onClick={() => setSelScreen('preview')}
              />
              <NameAndChangeToScreenCard
                title="Upload Solution (optional)"
                icon={<FileUploadIcon color={palette.secondary.main} />}
                onClick={() => setSelScreen('upload')}
              />
            </CardContent>
          </Card>
        </>
      ) : selectedScreen === 'preview' ? (
        <PreviewAsgn onBack={() => setSelScreen(null)} />
      ) : (
        <UploadAsgn
          onBack={() => {
            fetchAsgnData();
            setSelScreen(null);
          }}
          onFileSelect={(f) => updateAsgnWithSolution(f)}
        />
      )}
      <AddOrUpdateAssignment
        open={openEditDialog}
        editObj={asgnDetail}
        onClose={() => {
          setEditDialog(false);
          fetchAsgnData();
        }}
      />
    </div>
  );
}

UpcomingAsgnDetails.propTypes = {
  asgnId: PropTypes.string,
  onBack: PropTypes.func
};
